import {CarePlanConfigurationFeature} from '../../../../../backend/enums/care-plan-configuration-feature';
import {translations} from '../../../../../i18n/translations';
import {
  ExportCarePlanGroupDataProcessor,
  RowData,
} from '../base/export-care-plan-group-data-processor';
import {ExportCarePlanEntry, ExportCarePlanTableColumnStyle} from '../base/export-care-plan-types';

export class CommonPersonalDevelopmentXlsxDataProcessor extends ExportCarePlanGroupDataProcessor {
  private _additionalEntries: {id: string | string[]}[] = [];
  private activeAdditionalEntries: {id: string | string[]}[] = [];

  protected get additionalEntries() {
    return this._additionalEntries;
  }

  protected set additionalEntries(entries: {id: string | string[]}[]) {
    this._additionalEntries = entries;
    this.activeAdditionalEntries = entries.filter((additionalEntry) =>
      Boolean(this.getAdditionalEntryName(additionalEntry))
    );
  }

  get additionalEntry() {
    // Hack hardcoded ids: these entries don't have a feature set, so we look by id.
    return {
      audiovisualMaterials: {
        id: '6525515fa28d3dd4515abe26',
      },
      additionalMaterials: {
        id: '652552d30040ff8bd0edf0b5',
      },
      equipmentNeeded: {
        id: '65254ddf44c913b7c3be83f5',
      },
      meansOf: {
        id: '65254f9f288362b6f4816c76',
      },
      place: {
        id: ['6525564defc8891982a1f5f9', '652530573450bb2df6373c54', '652556eb3ff6ecf847d963f0'],
      },
      transport: {
        id: '65255763238f96255b312570',
      },
      visitType: {
        id: '6525581d1cdb6a6c80665dc6',
      },
      culinaryMaterial: {
        id: '65256194db0c3cfb64d08f74',
      },
      products: {
        id: '652561fc35ce7d45e078f906',
      },
      protectionEquipment: {
        id: '652561088ce6d98dbc15dc14',
      },
      animals: {
        id: '652562e956cea2f315e768cf',
      },
      domesticTasks: {
        id: '6525622d6eea460bc83a4937',
      },
      gardenMaterial: {
        id: '65255da26ecaa9caa977561a',
      },
      animalCare: {
        id: '652562ac8859847b2d622610',
      },
      sewingMaterial: {
        id: '6525626bd2151bd0eb0e1e1a',
      },
      contacts: {
        id: '65254d3778d8959bebab3be7',
      },
    };
  }

  override async resolveTaskHeaders(firstRowData: RowData): Promise<string[]> {
    const headers = firstRowData.entries
      .filter(
        (entry) =>
          !this.activeAdditionalEntries.some((additionalEntry) =>
            this.additionalEntryExists(entry, additionalEntry)
          )
      )
      .map((e) => e.name);
    const endDateHeader = this.t(
      translations.activity_type_posology_schedule_form.fields.end_date.label
    );
    const endHourHeader = this.t(
      translations.activity_type_posology_schedule_form.fields.end_hour.label
    );

    const headersOrdered = this.reorderCells(headers, endDateHeader, endHourHeader);

    return [
      ...headersOrdered.slice(0, -1),
      ...this.activeAdditionalEntries.map((additionalEntry) =>
        this.getAdditionalEntryName(additionalEntry)
      ),
      ...headersOrdered.slice(-1),
    ];
  }

  override resolveTaskRow(rowData: RowData): string[] {
    const cells: string[] = [];
    let endDateCell: string = '';
    let endHourCell: string = '';

    for (const entry of rowData.entries) {
      switch (entry.feature) {
        case CarePlanConfigurationFeature.Schedule: {
          const {periodicity, endDate, endHour} = this.resolveEntryCellSchedule(entry);
          cells.push(periodicity);
          endDateCell = endDate;
          endHourCell = endHour;
          break;
        }

        default:
          if (
            this.activeAdditionalEntries.some((additionalEntry) =>
              this.additionalEntryExists(entry, additionalEntry)
            )
          ) {
            // Hack*: Skip these, not all rows have them and we add them later bellow in the correct order.
            break;
          }
          cells.push(this.resolveEntryCell(entry));
          break;
      }
    }

    // HACK*: Additional cells
    const additionalCells: string[] = [];
    this.activeAdditionalEntries.forEach((additionalEntry) => {
      const entry = rowData.entries.find((e) => this.additionalEntryExists(e, additionalEntry));
      const value = this.getEntryValue(entry);
      additionalCells.push(value);
    });

    const cellsOrdered = this.reorderCells(cells, endDateCell, endHourCell);

    return [...cellsOrdered.slice(0, -1), ...additionalCells, ...cellsOrdered.slice(-1)];
  }

  override resolveTasksColumnStyles(
    taskHeaders: string[]
  ): ExportCarePlanTableColumnStyle | undefined {
    return taskHeaders.map((header, idx) => {
      return {cellWidth: idx < 6 || idx === taskHeaders.length - 1 ? 15 : 30};
    });
  }

  private additionalEntryExists(
    entry: ExportCarePlanEntry,
    additionalEntry: {id: string | string[]}
  ): boolean {
    return Array.isArray(additionalEntry.id)
      ? additionalEntry.id.includes(entry.id)
      : additionalEntry.id === entry.id;
  }

  private getAdditionalEntryName(additionalEntry: {id: string | string[]}): string {
    return this.getTaskEntryById(additionalEntry.id)?.name ?? '';
  }
}
